/** Console v0.5: the shared frame every desktop screen sits in (design
 * brief v0.5 s.3) -- sidebar, top bar and the content column. Built once;
 * screens only fill the content. */

import { accountLine, initials, navItems, navKeyOf, openCount, roleLabel, SETTINGS_SECTIONS, routePath } from "./routes.js";
import type { Route, V05Me } from "./routes.js";
import { button, figure, h, icon, menuButton, openDialog, uid } from "./ui.js";

export interface FrameOptions {
  me: V05Me;
  route: Route;
  /** The top bar's left side: the page name, or a breadcrumb (Organisations / name). */
  crumbs: ReadonlyArray<{ label: string; href?: string }>;
  navigate: (path: string) => void;
  onSignOut: () => void;
  onSwitchAccount?: (accountId: string) => void;
  timeZone: string;
}

const ICON = "/brand/preekie-icon.svg";

function clockTime(iso: string, timeZone: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat("en-AU", { hour: "2-digit", minute: "2-digit", hourCycle: "h23", timeZone }).format(d);
}

/** The open count on Review inbox. Ink, not coral: the screen's one coral is the Record a meeting dot (brief s.3). */
function inboxBadge(n: number): HTMLElement | null {
  if (n <= 0) return null;
  const badge = h("span", "pk5-badge pk5-figure", String(n));
  badge.setAttribute("aria-label", `${n} open`);
  return badge;
}

/** Updates just the Review inbox badge (after an inbox action), without redrawing the frame. */
export function setInboxBadge(n: number): void {
  const link = document.querySelector<HTMLAnchorElement>('.pk5-sidebar a.pk5-nav-item[href="/inbox"]');
  if (!link) return;
  link.querySelector(".pk5-badge")?.remove();
  const b = inboxBadge(n);
  if (b) link.append(b);
}

function navLink(label: string, href: string, current: boolean, extra?: Node | null): HTMLAnchorElement {
  const a = h("a", `pk5-nav-item${current ? " pk5-nav-on" : ""}`, h("span", "pk5-nav-label", label), extra ?? null);
  a.href = href;
  if (current) a.setAttribute("aria-current", "page");
  return a;
}

function sidebar(opts: FrameOptions, openSearch: () => void): HTMLElement {
  const { me, route } = opts;
  const nav = h("nav", "pk5-sidebar");
  nav.setAttribute("aria-label", "Main");
  nav.dataset.testid = "pk5-sidebar";

  // The `p` icon at the top of the rail (PRODUCT-UI.md s.2; the wordmark is for sign-in only).
  const logo = h("a", "pk5-logo");
  logo.href = "/";
  const img = h("img", "pk5-logo-icon");
  img.src = ICON;
  img.alt = "Preekie home";
  img.width = 32;
  img.height = 32;
  logo.append(img);
  nav.append(logo);

  if (me.account && me.is_super_admin !== true) {
    // The account switcher (brief s.5): which account and its type; a menu when the person belongs to more than one.
    const text = h("span", "pk5-account-text", h("span", "pk5-account-name", me.account.name), h("span", "pk5-account-sub", accountLine(me.account)));
    const others = (me.accounts ?? []).filter((a) => a.account_id !== me.account!.account_id);
    if (!others.length) {
      const acct = h("div", "pk5-account", text);
      acct.setAttribute("aria-label", `Account: ${me.account.name}, ${accountLine(me.account)}`);
      nav.append(acct);
    } else {
      const switcher = menuButton(
        `Switch account (now ${me.account.name})`,
        others.map((a) => ({ label: `${a.name} · ${a.type === "practice" ? "Practice" : "Company"}`, onSelect: () => opts.onSwitchAccount?.(a.account_id) }))
      );
      switcher.classList.add("pk5-account-switch");
      const trigger = switcher.querySelector("button")!;
      trigger.classList.add("pk5-account");
      trigger.replaceChildren(text, icon("chevron", 16));
      nav.append(switcher);
    }
  }

  const search = button(h("span", "pk5-search-text", "Search or jump to"), "secondary", openSearch);
  search.prepend(icon("search", 16));
  search.classList.add("pk5-search");
  search.append(h("kbd", "pk5-kbd pk5-figure", "Ctrl K"));
  search.setAttribute("aria-keyshortcuts", "Control+K");
  nav.append(search);

  const list = h("div", "pk5-nav");
  const currentKey = navKeyOf(route);
  for (const item of navItems(me)) {
    if (item.key === "super-admin") list.append(h("div", "pk5-nav-divider"));
    let extra: Node | null = null;
    if (item.key === "inbox") extra = inboxBadge(openCount(me));
    list.append(navLink(item.label, item.path, item.key === currentKey, extra));
    if (item.key === "settings" && currentKey === "settings" && route.kind === "settings") {
      const group = h("div", "pk5-subnav");
      group.setAttribute("role", "group");
      group.setAttribute("aria-label", "Settings sections");
      for (const s of SETTINGS_SECTIONS) {
        const a = h("a", `pk5-subnav-item${s.section === route.section ? " pk5-subnav-on" : ""}`, s.label);
        a.href = routePath({ kind: "settings", section: s.section });
        if (s.section === route.section) a.setAttribute("aria-current", "page");
        group.append(a);
      }
      list.append(group);
    }
  }
  nav.append(list);

  const pins = me.pinned_organisations ?? [];
  if (pins.length && me.is_super_admin !== true) {
    const id = uid("pk5-pins");
    const title = h("span", "pk5-pins-title", me.account?.type === "company" ? "Your organisation" : "Pinned organisations");
    title.id = id;
    const group = h("div", "pk5-pins", title);
    group.setAttribute("role", "group");
    group.setAttribute("aria-labelledby", id);
    for (const p of pins) {
      const a = h("a", "pk5-pin", h("span", "pk5-pin-ini", initials(p.name)), p.name);
      a.href = routePath({ kind: "organisation", id: p.organisation_id });
      group.append(a);
    }
    nav.append(group);
  }

  const who = me.display_name || me.email || "Signed in";
  const role = roleLabel(me);
  const user = h(
    "div",
    "pk5-user",
    h("span", "pk5-user-ini", initials(me.display_name, me.email)),
    h("span", "pk5-user-text", h("span", "pk5-user-name", who), role ? h("span", "pk5-user-role", role) : null),
    menuButton("Your account", [{ label: "Sign out", onSelect: opts.onSignOut }])
  );
  nav.append(user);
  return nav;
}

function topBar(opts: FrameOptions): HTMLElement {
  const bar = h("header", "pk5-topbar");
  bar.dataset.testid = "pk5-topbar";
  const crumbs = h("nav", "pk5-crumbs");
  crumbs.setAttribute("aria-label", "Breadcrumb");
  opts.crumbs.forEach((c, i) => {
    if (i > 0) crumbs.append(h("span", "pk5-crumb-sep", " / "));
    if (c.href) {
      const a = h("a", "pk5-crumb", c.label);
      a.href = c.href;
      crumbs.append(a);
    } else crumbs.append(h("span", "pk5-crumb pk5-crumb-current", c.label));
  });
  crumbs.lastElementChild?.setAttribute("aria-current", "page");
  const right = h("div", "pk5-topbar-right");
  if (opts.me.is_super_admin === true) {
    right.append(h("span", "pk5-sync", "Platform view · every action is logged"));
  } else if (opts.me.xero_synced_at) {
    const t = clockTime(opts.me.xero_synced_at, opts.timeZone);
    if (t) right.append(h("span", "pk5-sync", h("span", "pk5-sync-dot"), "Xero synced ", figure(t)));
  }
  const bell = button(icon("bell"), "secondary", () =>
    openDialog("Notifications", [h("p", null, "Nothing new right now.")], [{ label: "Close", variant: "secondary", safe: true, onClick: (close) => close() }])
  );
  bell.classList.add("pk5-icon-btn");
  bell.setAttribute("aria-label", "Notifications");
  right.append(bell);
  if (opts.me.is_super_admin !== true && opts.me.meetings_enabled === true) {
    const rec = h("a", "pk5-btn pk5-btn-primary pk5-record", h("span", "pk5-record-dot"), "Record a meeting");
    rec.href = routePath({ kind: "record", client: null });
    right.append(rec);
  }
  bar.append(crumbs, right);
  return bar;
}

/** Search or jump to (Ctrl K): the screens this person can open, and their pinned organisations. */
function openSearch(opts: FrameOptions): void {
  const input = h("input", "pk5-input");
  input.type = "search";
  input.placeholder = "Type a screen or organisation";
  input.setAttribute("aria-label", "Search or jump to");
  const results = h("ul", "pk5-search-results");
  const listId = uid("pk5-search-list");
  results.id = listId;
  input.setAttribute("aria-controls", listId);
  const targets = [
    ...navItems(opts.me).map((n) => ({ label: n.label, path: n.path })),
    ...(opts.me.pinned_organisations ?? []).map((p) => ({ label: p.name, path: routePath({ kind: "organisation", id: p.organisation_id }) })),
  ];
  let dialog: HTMLDialogElement | null = null;
  const draw = () => {
    const q = input.value.trim().toLowerCase();
    results.replaceChildren();
    for (const t of targets.filter((t) => !q || t.label.toLowerCase().includes(q))) {
      const a = h("a", "pk5-search-result", t.label);
      a.href = t.path;
      a.addEventListener("click", (ev) => {
        ev.preventDefault();
        dialog?.close();
        opts.navigate(t.path);
      });
      results.append(h("li", null, a));
    }
    if (!results.childElementCount) results.append(h("li", "pk5-search-none", "No matches."));
  };
  input.addEventListener("input", draw);
  draw();
  dialog = openDialog("Search or jump to", [input, results], [{ label: "Close", variant: "secondary", safe: true, onClick: (close) => close() }]);
  input.focus();
}

/** Draws the frame into root and returns the content column for the screen to fill. */
export function renderFrame(root: HTMLElement, opts: FrameOptions): HTMLElement {
  const skip = h("a", "pk5-skip", "Skip to content");
  skip.href = "#pk5-content";
  const content = h("div", "pk5-content");
  content.id = "pk5-content";
  content.dataset.testid = "pk5-content";
  content.tabIndex = -1;
  // The recorder's banner (a recording under way elsewhere, audio waiting on this computer) sits above every page.
  const bannerSlot = h("div", "pk5-banner-slot");
  const main = h("main", "pk5-main", topBar(opts), bannerSlot, content);
  root.replaceChildren(skip, sidebar(opts, () => openSearch(opts)), main);
  return content;
}

/** Ctrl K / Cmd K anywhere opens search (installed once). */
export function installShortcuts(open: () => void): void {
  document.addEventListener("keydown", (ev) => {
    if ((ev.ctrlKey || ev.metaKey) && ev.key.toLowerCase() === "k") {
      ev.preventDefault();
      open();
    }
  });
}

export { openSearch };
